/**
 * UpcomingReviewsWidget — dashboard card listing next N reviews across all modules
 * (Policies, Suppliers, DPIAs), sorted ASC by date.
 *
 * Data source: GET /api/compliance/calendar/reviews/upcoming?days=90&limit=5
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CalendarCheck, Loader2, FileText, Users, ShieldCheck, ArrowRight, AlertTriangle, RefreshCw, Download, Calendar as CalIcon, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_META = {
  policy:   { label: 'Política',    icon: FileText,       tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  supplier: { label: 'Fornecedor',  icon: Users,          tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  dpia:     { label: 'DPIA',        icon: ShieldCheck,    tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  audit:    { label: 'Auditoria',   icon: ClipboardCheck, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  bcm:      { label: 'BCM',         icon: RefreshCw,      tone: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
};

const MODULE_FILTERS = [
  { value: 'all',      label: 'Todos' },
  { value: 'policy',   label: 'Políticas' },
  { value: 'supplier', label: 'Fornecedores' },
  { value: 'dpia',     label: 'DPIAs' },
  { value: 'audit',    label: 'Auditorias' },
  { value: 'bcm',      label: 'BCM' },
];

function daysLabel(d) {
  if (d < 0) return `${Math.abs(d)}d em atraso`;
  if (d === 0) return 'Hoje';
  if (d === 1) return 'Amanhã';
  if (d <= 30) return `em ${d}d`;
  return `em ${Math.round(d / 30)} meses`;
}

export function UpcomingReviewsWidget({ days = 90, limit = 5 }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/compliance/calendar/reviews/upcoming', { params: { days, limit: 100 } });
      setItems(res.data.items || []);
    } catch {
      toast.error('Falha ao carregar revisões próximas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);

  const filteredItems = moduleFilter === 'all'
    ? items.slice(0, limit)
    : items.filter(it => it.module === moduleFilter).slice(0, limit);
  const moduleCounts = items.reduce((acc, it) => {
    acc[it.module] = (acc[it.module] || 0) + 1;
    return acc;
  }, {});

  const runScanner = async () => {
    setRunning(true);
    try {
      const res = await api.post('/compliance/calendar/reviews/check');
      toast.success(`Verificação concluída · ${res.data.tasks_created} tarefa(s), ${res.data.alerts_created} alerta(s)`);
      load();
    } catch {
      toast.error('Falha ao executar verificação.');
    } finally {
      setRunning(false);
    }
  };

  const downloadFile = async (format) => {
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${BACKEND_URL}/api/compliance/calendar/reviews/upcoming.${format}?days=365`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('http');
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      const now = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = format === 'xlsx' ? `revisoes-proximas-${now}.xlsx` : `revisoes-nis2ai-${now}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(format === 'xlsx' ? 'Excel exportado.' : 'Calendário .ics exportado.');
    } catch {
      toast.error('Falha ao exportar.');
    }
  };

  return (
    <Card data-testid="upcoming-reviews-widget">
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarCheck className="h-4 w-4 text-indigo-600" /> Revisões próximas
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadFile('xlsx')}
              data-testid="reviews-export-xlsx"
              title="Exportar para Excel"
              className="h-7 px-2"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadFile('ics')}
              data-testid="reviews-export-ics"
              title="Exportar para calendário (Outlook / Google / Apple)"
              className="h-7 px-2"
            >
              <CalIcon className="h-3.5 w-3.5 mr-1" /> .ics
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={runScanner}
                disabled={running}
                data-testid="reviews-scan-btn"
                title="Verificar todas as revisões e criar tarefas se necessário"
                className="h-7 px-2"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Module filter pills */}
        <div className="flex items-center gap-1 flex-wrap px-3 py-2 border-b border-slate-100 bg-slate-50/50" data-testid="reviews-module-filter">
          {MODULE_FILTERS.map(f => {
            const count = f.value === 'all' ? items.length : (moduleCounts[f.value] || 0);
            return (
              <button
                key={f.value}
                onClick={() => setModuleFilter(f.value)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  moduleFilter === f.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-300'
                }`}
                data-testid={`reviews-filter-${f.value}`}
              >
                {f.label} <span className="opacity-70 tabular-nums">({count})</span>
              </button>
            );
          })}
        </div>
        {loading ? (
          <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></div>
        ) : filteredItems.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">
            {moduleFilter === 'all'
              ? `Sem revisões previstas nos próximos ${days} dias.`
              : `Sem revisões de ${MODULE_META[moduleFilter]?.label} nos próximos ${days} dias.`}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredItems.map(it => {
              const meta = MODULE_META[it.module] || MODULE_META.policy;
              const Icon = meta.icon;
              return (
                <Link
                  key={`${it.module}-${it.entity_id}`}
                  to={it.route || '/'}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                  data-testid={`upcoming-review-${it.module}-${it.entity_id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.tone}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className={`${meta.tone} text-[10px]`}>{meta.label}</Badge>
                      {it.overdue && (
                        <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200 text-[10px]">
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Atraso
                        </Badge>
                      )}
                      <span className={`text-[11px] tabular-nums ${it.overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {daysLabel(it.days_until)}
                      </span>
                    </div>
                    <div className="text-sm text-slate-800 truncate mt-0.5">{it.title}</div>
                    <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">{it.date}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default UpcomingReviewsWidget;
